import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import { withTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  getDashboardAnalytics,
  getRevenueTrends,
  exportDashboardAnalytics,
} from "../../Endpoint/Admin";
import { downloadBase64File } from "../../utils/exportToExcel";
import { ConsolePage } from "../../components/Console/ConsoleLayout";
import {
  C,
  Panel,
  StatCard,
  StatRow,
  Btn,
  PanelLink,
} from "../../components/Console/ConsoleUI";
import { hasModuleAccess } from "../../services/PermissionService";
import PeriodFilter, { thisMonth, periodToParams, periodLabel } from "../../components/Common/PeriodFilter";

/**
 * Revenue & insights — screen E6 of the console redesign.
 *
 * Four KPIs, then the revenue split beside a rail of the smaller numbers and
 * the deeper reports. Both requests are the ones this screen already made
 * (/analytics dashboard summary and revenue trends), both given the period
 * from the shared PeriodFilter; the export gets the same one.
 *
 * The handoff draws this split *by branch*, with RevPAR, F&B-per-guest and
 * transport margin as its KPIs. The dashboard endpoint returns none of those:
 * it reports revenue by service (food / rooms / transport) and counts of
 * orders, bookings and requests. Rather than invent hotel metrics or fire a
 * second request, the same stacked-bar treatment is applied to the split the
 * API actually returns, and it is labelled by service.
 */

const money = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n || 0);

const count = (n) => new Intl.NumberFormat("en-US").format(n || 0);

/** StatCard reads a leading "-" as a fall, so growth is formatted with one. */
const delta = (g) => {
  if (g == null || Number.isNaN(g)) return null;
  return `${g < 0 ? "-" : "+"}${Math.abs(g).toFixed(1)}%`;
};

const SERVICES = [
  { key: "food", label: "Dining", color: C.bronze, icon: "restaurant" },
  { key: "room", label: "Rooms", color: C.brown, icon: "bed" },
  { key: "transport", label: "Transport", color: C.green, icon: "local_taxi" },
];

const REPORTS = [
  { to: "/analytics/revenue", icon: "payments", label: "Revenue" },
  { to: "/analytics/food", icon: "restaurant", label: "Food" },
  { to: "/analytics/room", icon: "bed", label: "Rooms" },
  { to: "/analytics/table", icon: "table_restaurant", label: "Tables" },
  { to: "/analytics/transport", icon: "local_taxi", label: "Transport" },
  { to: "/analytics/coupon", icon: "sell", label: "Coupons" },
  { to: "/analytics/users", icon: "group", label: "Guests" },
];

class AnalyticsDashboard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      exporting: false,
      period: thisMonth(),
      dashboardData: null,
      trendsData: null,
    };
  }

  componentDidMount() {
    this.loadDashboardData();
  }

  loadDashboardData = async () => {
    this.setState({ loading: true });
    const { period } = this.state;

    const params = periodToParams(period);
    const [dashboardResponse, trendsResponse] = await Promise.all([
      getDashboardAnalytics(params),
      getRevenueTrends(params),
    ]);

    this.setState({
      dashboardData: dashboardResponse?.success ? dashboardResponse.data : null,
      trendsData: trendsResponse?.success ? trendsResponse.data : null,
      loading: false,
    });
  };

  handlePeriodChange = (period) => {
    this.setState({ period }, this.loadDashboardData);
  };

  handleExport = async () => {
    this.setState({ exporting: true });
    try {
      const res = await exportDashboardAnalytics(periodToParams(this.state.period));
      if (res?.success && res.data) {
        downloadBase64File(res.data.base64, res.data.fileName);
      } else {
        toast.error("Export failed");
      }
    } catch (err) {
      toast.error("Export failed");
    }
    this.setState({ exporting: false });
  };

  // --- presentation ---------------------------------------------------------

  /** Revenue by service, with each service's share of the total. */
  serviceRows() {
    const breakdown = this.state.dashboardData?.revenueBreakdown || {};
    const growth = this.state.trendsData?.growth || {};
    const total = SERVICES.reduce((s, sv) => s + (breakdown[sv.key] || 0), 0);
    return SERVICES.map((sv) => {
      const value = breakdown[sv.key] || 0;
      return {
        ...sv,
        value,
        share: total > 0 ? (value / total) * 100 : 0,
        growth: growth[sv.key],
      };
    });
  }

  renderSplitPanel() {
    const rows = this.serviceRows();
    const total = rows.reduce((s, r) => s + r.value, 0);
    const { period } = this.state;

    return (
      <Panel
        title={`Revenue by service · ${periodLabel(period)}`}
        subtitle="USD, across the branch scope in effect"
        actions={
          <div style={{ display: "flex", alignItems: "center", gap: 16, fontSize: 13, color: C.slate }}>
            {rows.map((r) => (
              <span key={r.key} style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <span style={{ width: 10, height: 10, borderRadius: 3, background: r.color }} />
                {r.label}
              </span>
            ))}
          </div>
        }
      >
        <div style={{ marginTop: 18, display: "flex", alignItems: "baseline", gap: 12 }}>
          <div style={{ fontSize: 32, fontWeight: 500, color: C.ink }}>{money(total)}</div>
          <div style={{ fontSize: 14, color: C.taupe }}>total across the three services</div>
        </div>

        {/* One stacked bar for the whole period, then the same numbers broken
            out per service — the bar and the rows can never disagree because
            they read from the same array. */}
        <div style={{ marginTop: 18, display: "flex", height: 16, borderRadius: 8, overflow: "hidden" }}>
          {total > 0 ? (
            rows.map((r) => (
              <div key={r.key} style={{ width: `${r.share}%`, background: r.color }} />
            ))
          ) : (
            <div style={{ flex: 1, background: "#f2e7d7" }} />
          )}
        </div>

        <div style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 20 }}>
          {rows.map((r) => {
            // A "+0.0%" beside a service that took nothing reads as a rise it
            // did not have, so a line with no revenue carries no trend.
            const d = r.value > 0 ? delta(r.growth) : null;
            const down = d && d.startsWith("-");
            return (
              <div key={r.key}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", fontSize: 14, color: C.ink }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span className="ms" style={{ fontSize: 18, color: C.taupeLight }}>{r.icon}</span>
                    {r.label}
                  </span>
                  <span style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
                    {d && (
                      <span style={{ fontSize: 13, color: down ? C.red : C.green }}>{d}</span>
                    )}
                    <span style={{ fontWeight: 500 }}>{money(r.value)}</span>
                  </span>
                </div>
                <div style={{ marginTop: 8, height: 16, borderRadius: 8, background: "#f2e7d7", overflow: "hidden" }}>
                  <div style={{ width: `${r.share}%`, height: "100%", background: r.color }} />
                </div>
              </div>
            );
          })}
          {total === 0 && (
            <div style={{ fontSize: 14, color: C.taupeLight }}>
              No revenue recorded in this period.
            </div>
          )}
        </div>
      </Panel>
    );
  }

  render() {
    const { loading, period, dashboardData, trendsData, exporting } = this.state;
    const kpis = dashboardData?.kpis || {};
    const growth = trendsData?.growth || {};

    return (
      <ConsolePage
        title="Revenue & insights"
        subtitle={loading ? "Loading figures…" : `${periodLabel(period)} · updated just now`}
        actions={
          <Btn icon="download" onClick={this.handleExport} disabled={exporting}>
            {exporting ? "Exporting…" : "Export"}
          </Btn>
        }
      >
        {/* Above the loading state, so the period stays changeable while figures load. */}
        <PeriodFilter value={period} onChange={this.handlePeriodChange} style={{ marginBottom: 20 }} />
        {loading ? (
          <div style={{ padding: "80px 0", textAlign: "center", color: C.taupeLight }}>
            Loading analytics…
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <StatRow>
              <StatCard
                icon="payments"
                value={money(kpis.totalRevenue)}
                label="Total revenue"
                delta={delta(growth.total)}
              />
              <StatCard icon="restaurant" value={count(kpis.foodOrders)} label="Food orders" />
              <StatCard icon="bed" value={count(kpis.roomBookings)} label="Room bookings" />
              <StatCard
                icon="local_taxi"
                value={count(kpis.transportRequests)}
                label="Transport requests"
              />
            </StatRow>

            <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: 18, alignItems: "start" }}>
              {this.renderSplitPanel()}

              <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                <Panel title="Also this period">
                  <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, color: "#3a3a3a" }}>
                      <span>Table bookings</span>
                      <span style={{ color: C.taupe }}>{count(kpis.tableBookings)}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, color: "#3a3a3a" }}>
                      <span>New guests</span>
                      <span style={{ color: C.taupe }}>{count(kpis.newUsers)}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, color: "#3a3a3a" }}>
                      <span>Transport requests</span>
                      <span style={{ color: C.taupe }}>{count(kpis.transportRequests)}</span>
                    </div>
                  </div>
                </Panel>

                <Panel
                  title="Detailed reports"
                  style={{ display: "flex", flexDirection: "column", minHeight: 300 }}
                >
                  <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 2 }}>
                    {REPORTS.map((r) => (
                      <button
                        key={r.to}
                        type="button"
                        onClick={() => this.props.history.push(r.to)}
                        style={{
                          display: "flex", alignItems: "center", gap: 12, height: 40,
                          padding: "0 10px", borderRadius: 12, border: 0,
                          background: "transparent", color: C.slate, fontSize: 14,
                          fontFamily: "inherit", cursor: "pointer", textAlign: "left", width: "100%",
                        }}
                      >
                        <span className="ms" style={{ fontSize: 19, color: C.taupeLight }}>{r.icon}</span>
                        {r.label}
                        <span className="ms" style={{ marginLeft: "auto", fontSize: 18, color: C.sand }}>
                          chevron_right
                        </span>
                      </button>
                    ))}
                  </div>
                  {/* Only offered to an account that can actually open it —
                      payments is a separate module gate. */}
                  {hasModuleAccess("PAYMENTS") && (
                    <PanelLink onClick={() => this.props.history.push("/payments/transactions")}>
                      Open payments ledger
                    </PanelLink>
                  )}
                </Panel>
              </div>
            </div>
          </div>
        )}
      </ConsolePage>
    );
  }
}

export default withRouter(withTranslation()(AnalyticsDashboard));
